import { useTranslation } from 'react-i18next'
import { JSX, RefObject, useEffect, useRef, useState } from 'react'
import {
    Box,
    Chip,
    Divider,
    FormControl,
    InputLabel,
    ListSubheader,
    MenuProps,
    OutlinedInput,
    Select,
    SelectChangeEvent
} from '@mui/material'
import {
    ACTION_ITEM_VALUE,
    OptionsGroup,
    SelectActionItem,
    SelectOption,
    SelectOptions,
    ValueType
} from '@/types/Select'
import MenuItem from '@mui/material/MenuItem'
import styles from './selectMultiple.module.scss'
import globalStyles from '@/styles/components.module.scss'
import Checkbox from '../../Checkbox/Checkbox'

interface SelectMultipleProps<T extends ValueType> {
    label?: string
    noOptionMessage?: string
    selectOptions: SelectOptions<T>
    selectValues: T[]
    getValueKey?: (value: T) => string
    setSelectValues: (values: T[]) => void
    readOnly?: boolean
    fullWidth?: boolean
    isParent?: boolean
    className?: string
    oneLine?: boolean
    maxToShow?: number
    maxSelected?: number
    actionItem?: SelectActionItem
    reopenTrigger?: number
    error?: boolean
    onOpen?: () => void
}

function SelectMultiple<T extends ValueType>({
    label,
    noOptionMessage,
    selectOptions = [],
    selectValues,
    getValueKey = String,
    setSelectValues,
    fullWidth = true,
    readOnly = false,
    isParent = false,
    className,
    oneLine = false,
    maxToShow,
    maxSelected,
    actionItem,
    reopenTrigger,
    error = false,
    onOpen
}: SelectMultipleProps<T>): JSX.Element {
    const { t } = useTranslation()

    const [open, setOpen] = useState<boolean>(false)

    function handleOpen(): void {
        onOpen?.()
        setOpen(true)
    }

    const shouldReopen: RefObject<boolean> = useRef(false)
    const isFirstReopenRender: RefObject<boolean> = useRef(true)

    useEffect((): void => {
        if (isFirstReopenRender.current) {
            isFirstReopenRender.current = false
            return
        }
        if (shouldReopen.current) {
            shouldReopen.current = false
            handleOpen()
        }
    }, [reopenTrigger])

    function handleChange(e: SelectChangeEvent<string[]>): void {
        const value: string[] = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value

        if (value.includes(ACTION_ITEM_VALUE)) {
            setOpen(false)
            shouldReopen.current = true
            actionItem?.onClick()
            return
        }

        if (maxSelected && value.length > maxSelected) {
            return
        }
        const options: SelectOption<T>[] = selectOptions.flatMap((group) => group.options)
        const selectedValues: T[] = []
        for (const selectedValue of value) {
            const option: SelectOption<T> | undefined = options.find(
                (option) => getValueKey(option.value) === selectedValue
            )
            if (option) selectedValues.push(option.value)
        }
        setSelectValues(selectedValues)
    }

    const ITEM_HEIGHT: number = 48
    const ITEM_PADDING_TOP: number = 8
    const MenuProps: Partial<MenuProps> = {
        slotProps: {
            paper: {
                style: {
                    maxHeight: ITEM_HEIGHT * 8.5 + ITEM_PADDING_TOP,
                    maxWidth: 500
                }
            }
        }
    }

    function findOption(selectOptions: SelectOptions<T>, value: T): SelectOption<T> | null {
        for (const group of selectOptions) {
            const found: SelectOption<T> | undefined = group.options.find(
                (option: SelectOption<T>): boolean => getValueKey(option.value) === getValueKey(value)
            )
            if (found) return found
        }
        return null
    }

    function renderOption(option: SelectOption<T>): JSX.Element {
        const isChecked: boolean = selectValues.some((value) => getValueKey(value) === getValueKey(option.value))
        return (
            <MenuItem
                key={getValueKey(option.value)}
                value={getValueKey(option.value)}
                className={styles.MenuItem}
                disabled={option.disabled}
                sx={{ whiteSpace: 'normal' }}
            >
                <Checkbox
                    checked={isChecked}
                    text={option.name}
                    parent={isParent}
                    disabled={!isChecked && maxSelected !== undefined && selectValues.length >= maxSelected}
                />
            </MenuItem>
        )
    }

    function renderOptions(group: OptionsGroup<T>): (JSX.Element | JSX.Element[] | null)[] {
        return [
            group.name ? <ListSubheader key={`header-${group.name}`}>{group.name}</ListSubheader> : null,
            ...group.options.map((option) => renderOption(option))
        ]
    }

    const allOptions: (JSX.Element | JSX.Element[] | null)[] = selectOptions.flatMap(renderOptions)

    if (!selectOptions.some((group) => group.options.length > 0)) {
        allOptions.push(
            <MenuItem key='no-options' disabled>
                {noOptionMessage ?? t('noOptionsAvailable')}
            </MenuItem>
        )
    }

    if (actionItem) {
        allOptions.push(
            <Divider key='action-item-divider' />,
            <MenuItem key='action-item' value={ACTION_ITEM_VALUE} className={styles.ActionItem}>
                {actionItem.icon}
                {actionItem.label}
            </MenuItem>
        )
    }

    return (
        <FormControl
            disabled={readOnly}
            error={error}
            className={`${className} ${fullWidth && styles.FullWidth} ${isParent ? styles.Parent : styles.Daycare} ${error ? globalStyles.FieldInError : ''}`}
        >
            {label && <InputLabel id='demo-multiple-chip-label'>{label}</InputLabel>}
            <Select
                labelId='demo-multiple-chip-label'
                id='demo-multiple-chip'
                multiple
                open={open}
                onOpen={handleOpen}
                onClose={() => setOpen(false)}
                value={selectValues.map(getValueKey)}
                onChange={handleChange}
                input={<OutlinedInput label={label} />}
                renderValue={(): string | JSX.Element =>
                    oneLine ? (
                        selectValues
                            .map((value: T): string | undefined => findOption(selectOptions, value)?.name)
                            .join(', ')
                    ) : maxToShow ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selectValues.slice(0, maxToShow).map((value: T): JSX.Element => {
                                const option: SelectOption<T> | null = findOption(selectOptions, value)
                                return <Chip key={getValueKey(value)} label={option?.name ?? ''} />
                            })}
                            {selectValues.length > maxToShow && <Chip label={`+${selectValues.length - maxToShow}`} />}
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selectValues.map((value: T): JSX.Element => {
                                const option: SelectOption<T> | null = findOption(selectOptions, value)
                                return <Chip key={getValueKey(value)} label={option?.name ?? ''} />
                            })}
                        </Box>
                    )
                }
                MenuProps={MenuProps}
                readOnly={readOnly}
                variant={'outlined'}
            >
                {allOptions}
            </Select>
        </FormControl>
    )
}

export default SelectMultiple
